import LockIcon from '@mui/icons-material/LockRounded';
import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Stack from '@zenbusiness/zui/Stack';

import CalloutAsset from '#client/components/CalloutAsset';
import CalloutIcon from '#client/components/CalloutIcon';
import ContactInfoForm from '#client/components/ContactInfoForm';
import PageHeader from '#client/components/PageHeader';
import ReviewsCard from '#client/components/ReviewsCard';
import ShouldLoginDialog, { useShouldLoginDialog } from '#client/components/ShouldLoginDialog';
import useFlowState from '#client/hooks/flow/useFlowState';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';

import { useSaveContactInfo } from './hooks/useSaveContactInfo';

function ContactInfoPage(props) {
  const { disableSkip, messageKey = 'contactInfo', slotProps } = props;

  const { contactInfo } = useFlowState();
  const { formatMessage } = useIntl();

  const { isShouldLoginDialogOpen, openShouldLoginDialog, closeShouldLoginDialog, handleLogin } =
    useShouldLoginDialog();

  const { handleSkip, handleSubmit } = useSaveContactInfo({
    onLoginPrompt: openShouldLoginDialog
  });

  const {
    calloutAsset: calloutAssetProps = {},
    calloutIcon: calloutIconProps = {},
    pageHeader: pageHeaderProps = {},
    reviewsCard: reviewsCardProps = {}
  } = slotProps || {};

  const MESSAGE_KEYS = {
    calloutAsset: calloutAssetProps.messageKey || messageKey,
    calloutIcon: calloutIconProps.messageKey || messageKey,
    pageHeader: pageHeaderProps.messageKey || messageKey,
    reviewsCard: reviewsCardProps.messageKey || messageKey
  };

  return (
    <>
      <TwoColumnLayout>
        <TwoColumnLayout.SmallColumn>
          <PageHeader
            subtitle={formatMessage({
              id: `shopping.page.${MESSAGE_KEYS.pageHeader}.subtitle`
            })}
            title={formatMessage({
              id: `shopping.page.${MESSAGE_KEYS.pageHeader}.title`
            })}
          />

          <ContactInfoForm
            defaultValues={contactInfo}
            onSubmit={handleSubmit}
            {...(!disableSkip && { onSkip: handleSkip })}
          />

          <CalloutIcon
            description={formatMessage({
              id: `shopping.page.${MESSAGE_KEYS.calloutIcon}.callout.subtitle`
            })}
            icon={<LockIcon sx={{ fontSize: 32 }} />}
            title={formatMessage({
              id: `shopping.page.${MESSAGE_KEYS.calloutIcon}.callout.title`
            })}
          />
        </TwoColumnLayout.SmallColumn>

        <TwoColumnLayout.LargeColumn>
          <Stack spacing={2}>
            <ReviewsCard
              review={formatMessage({
                id: `shopping.page.${MESSAGE_KEYS.reviewsCard}.review.content`
              })}
              reviewer={formatMessage({
                id: `shopping.page.${MESSAGE_KEYS.reviewsCard}.review.name`
              })}
              starCount={5}
            />
            <CalloutAsset name="funnel-info" {...calloutAssetProps} />
          </Stack>
        </TwoColumnLayout.LargeColumn>
      </TwoColumnLayout>

      <ShouldLoginDialog
        open={isShouldLoginDialogOpen}
        onClose={closeShouldLoginDialog}
        onLogin={handleLogin}
      />
    </>
  );
}

ContactInfoPage.propTypes = {
  /** Disable the skip button. */
  disableSkip: PropTypes.bool,
  /** The key to use for the intl messages. Defaults to 'contactInfo'. */
  messageKey: PropTypes.string,
  /** The slot props for the component. */
  slotProps: PropTypes.shape({
    calloutAsset: PropTypes.object,
    calloutIcon: PropTypes.object,
    pageHeader: PropTypes.object,
    reviewsCard: PropTypes.object
  })
};

export default ContactInfoPage;
